import { useMemo, useRef } from 'react';
import {
  getFragment,
  InitialPrimarySearchResultsDocument,
  PrimarySearchResultsDocument,
  SearchResultArtistFragmentDoc,
  SearchResultCollectorFragmentDoc,
  SearchResultReleaseFragmentDoc,
  SearchResultShelfFragmentDoc,
  useQuery,
} from '@graphql/reactQuery';
import type { useSearchInput } from './useSearchInput';

type UsePrimarySearchResultsProps = Pick<
  ReturnType<typeof useSearchInput>,
  'input' | 'isEmptyInputText' | 'text'
>;

export function usePrimarySearchResults({
  input,
  isEmptyInputText,
  text,
}: UsePrimarySearchResultsProps) {
  const { data: initialResultsData, isLoading: initialLoading } = useQuery(
    InitialPrimarySearchResultsDocument,
    {
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  );
  const { data, isLoading: searchLoading } = useQuery(PrimarySearchResultsDocument, {
    variables: {
      input,
    },
    staleTime: Infinity,
    enabled: !!text,
  });

  const loading = isEmptyInputText ? initialLoading : searchLoading;

  const latestDataRef = useRef(data);

  // When text is empty, we reset the `latestData` reference
  if (isEmptyInputText) {
    latestDataRef.current = undefined;
  }

  // We update the `latestData` reference when `data` exists
  if (data != null) {
    latestDataRef.current = data;
  }

  const latestData = latestDataRef.current;

  const artistResults = useMemo(() => {
    return isEmptyInputText || !latestData
      ? initialResultsData?.data.artists.edges.map(value =>
          getFragment(SearchResultArtistFragmentDoc, value.node),
        ) ?? []
      : latestData?.data.search.artistsPaginated.edges.map(value =>
          getFragment(SearchResultArtistFragmentDoc, value.node),
        ) ?? [];
  }, [latestData, initialResultsData, isEmptyInputText]);

  const releaseResults = useMemo(() => {
    return isEmptyInputText || !latestData
      ? initialResultsData?.data.releases.edges.map(value =>
          getFragment(SearchResultReleaseFragmentDoc, value.node),
        ) ?? []
      : latestData?.data.search.releasesPaginated.edges.map(value =>
          getFragment(SearchResultReleaseFragmentDoc, value.node),
        ) ?? [];
  }, [latestData, initialResultsData, isEmptyInputText]);

  const playlistResults = useMemo(() => {
    return isEmptyInputText || !latestData
      ? initialResultsData?.data.allShelves.edges.map(value =>
          getFragment(SearchResultShelfFragmentDoc, value.node),
        ) ?? []
      : latestData.data.search.shelves.edges.map(value =>
          getFragment(SearchResultShelfFragmentDoc, value.node),
        );
  }, [latestData, initialResultsData, isEmptyInputText]);

  const collectorResults = useMemo(() => {
    return isEmptyInputText || !latestData
      ? initialResultsData?.data.allCollectors.edges.map(value =>
          getFragment(SearchResultCollectorFragmentDoc, value.node),
        ) ?? []
      : latestData.data.search.collectors.edges.map(value =>
          getFragment(SearchResultCollectorFragmentDoc, value.node),
        );
  }, [latestData, initialResultsData, isEmptyInputText]);

  return {
    artistResults,
    releaseResults,
    playlistResults,
    collectorResults,
    isLoading: loading,
  };
}
